import { SdkContext } from '../internal'
import { listItems } from '@/lib/utils'
import type {
  DownloadConfirmResult,
  DownloadItem,
  DownloadItemInput,
  DownloadLink,
  DownloadListParams,
  DownloadPage,
  DownloadSpaceConfig,
  DownloadSpaceInput,
  DownloadStats,
  DownloadStreamLink,
  DownloadUpload,
  PendingDownloadItem
} from '@/types/downloads'

const toPage = <T>(result: unknown): DownloadPage<T> => {
  const page = result as Partial<DownloadPage<T>> | null
  return { items: listItems<T>(result), pagination: page?.pagination ?? null }
}

/** F-75: the Downloads module — space terms, items, files, the signed link, "my downloads" and moderation. Paths are written by hand (F-03). */
export class DownloadsResource {
  constructor(private ctx: SdkContext) {}

  /* ------------------------------------------------------------- space -- */

  async getSpace(spaceId: string): Promise<DownloadSpaceConfig> {
    return this.ctx.call({ httpMethod: 'GET', path: '/downloads/spaces/{spaceId}' }, { spaceId })
  }

  async updateSpace(spaceId: string, body: DownloadSpaceInput): Promise<DownloadSpaceConfig> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/downloads/spaces/{spaceId}', hasBody: true }, { spaceId, body })
  }

  /* ------------------------------------------------------------- items -- */

  async listItems(params: DownloadListParams): Promise<DownloadPage<DownloadItem>> {
    const { spaceId, mine, categoryId, q, status, page, limit } = params
    return toPage(
      await this.ctx.raw(
        { httpMethod: 'GET', path: '/downloads/items' },
        { query: { spaceId, mine: mine || undefined, categoryId, q: q?.trim() || undefined, status, page, limit } }
      )
    )
  }

  async getItem(id: string): Promise<DownloadItem> {
    return this.ctx.call({ httpMethod: 'GET', path: '/downloads/items/{id}' }, { id })
  }

  async createItem(body: DownloadItemInput & { spaceId: string; title: string }): Promise<DownloadItem> {
    return this.ctx.call({ httpMethod: 'POST', path: '/downloads/items', hasBody: true }, { body })
  }

  async updateItem(id: string, body: DownloadItemInput): Promise<DownloadItem> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/downloads/items/{id}', hasBody: true }, { id, body })
  }

  async deleteItem(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/downloads/items/{id}' }, { id })
  }

  async stats(id: string): Promise<DownloadStats> {
    return this.ctx.call({ httpMethod: 'GET', path: '/downloads/items/{id}/stats' }, { id })
  }

  /* ------------------------------------------------------------- files -- */

  /** Step 1 of an upload: a signed `PUT` bound to the exact type and size. */
  async requestUpload(id: string, file: { name: string; mimeType: string; size: number }): Promise<DownloadUpload> {
    return this.ctx.call({ httpMethod: 'POST', path: '/downloads/items/{id}/files/upload-url', hasBody: true }, { id, body: file })
  }

  /** Step 3: the API checks the object (size, type, first bytes) and links it to the item. */
  async confirmFile(id: string, body: { key: string; name: string; size: number }): Promise<DownloadConfirmResult> {
    return this.ctx.call({ httpMethod: 'POST', path: '/downloads/items/{id}/files/confirm', hasBody: true }, { id, body })
  }

  async replaceFile(id: string, fileId: string, body: { key: string; name: string; size: number }): Promise<DownloadConfirmResult> {
    return this.ctx.call({ httpMethod: 'POST', path: '/downloads/items/{id}/files/{fileId}/replace', hasBody: true }, { id, fileId, body })
  }

  async reorderFiles(id: string, fileIds: string[]): Promise<DownloadItem> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/downloads/items/{id}/files/reorder', hasBody: true }, { id, body: { fileIds } })
  }

  async removeFile(id: string, fileId: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/downloads/items/{id}/files/{fileId}' }, { id, fileId })
  }

  /** A link that lasts 5 minutes, made on every request. Use it right away; never keep it. */
  async download(fileId: string): Promise<DownloadLink> {
    return this.ctx.call({ httpMethod: 'POST', path: '/downloads/files/{fileId}/download', hasBody: true }, { fileId })
  }

  /** The player's link for an audio or MP4 file (3 hours, `inline`). Playing is not a download; ask again when it expires. */
  async streamUrl(fileId: string): Promise<DownloadStreamLink> {
    return this.ctx.call({ httpMethod: 'GET', path: '/downloads/files/{fileId}/stream-url' }, { fileId })
  }

  /* ------------------------------------------------------- my downloads -- */

  async listMine(page = 1, limit = 50): Promise<DownloadPage<DownloadItem>> {
    return toPage(await this.ctx.raw({ httpMethod: 'GET', path: '/members/me/downloads' }, { query: { page, limit } }))
  }

  /* -------------------------------------------------------- moderation -- */

  async listPending(page = 1, limit = 50): Promise<DownloadPage<PendingDownloadItem>> {
    return toPage(await this.ctx.raw({ httpMethod: 'GET', path: '/moderation/downloads/pending' }, { query: { page, limit } }))
  }

  async moderate(id: string, status: 'APPROVED' | 'REJECTED', rejectionReason?: string): Promise<void> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/moderation/downloads/{id}', hasBody: true },
      { id, body: { status, ...(rejectionReason ? { rejectionReason } : {}) } }
    )
  }
}
